import { lazy, PropsWithChildren, Suspense } from 'react';
import { Navigate, RouteObject, createBrowserRouter, useParams } from 'react-router-dom';
import MainLayoutProvider from 'providers/MainLayoutProvider';
import App from 'App';
import { ConfirmProvider } from 'providers/ConfirmProvider';
import PageFallback from 'layouts/PageFallback';
import { isPwa } from 'hooks/usePwaDetect';
import { getItemFromStore } from 'helpers/utils';
import ProjectGuard from 'components/common/ProjectGuard';

// Lazy-loaded layouts
const FrontPageLayout = lazy(() => import('layouts/FrontPageLayout'));
const DashboardLayout = lazy(() => import('layouts/DashboardLayout'));

// Lazy-loaded frontpage pages
const Home = lazy(() => import('pages/frontpage/Home'));
const Pricing = lazy(() => import('pages/frontpage/Pricing'));
const Product = lazy(() => import('pages/frontpage/Product'));
const Contact = lazy(() => import('pages/frontpage/Contact'));
const AskAssistant = lazy(() => import('pages/frontpage/AskAssistant'));
const PrivacyPolicy = lazy(() => import('pages/frontpage/PrivacyPolicy'));
const TermsOfService = lazy(() => import('pages/frontpage/TermsOfService'));

// Lazy-loaded dashboard pages
const Projects = lazy(() => import('pages/dashboard/Projects'));
const Tasks = lazy(() => import('pages/dashboard/Tasks'));
const Members = lazy(() => import('pages/dashboard/Members'));
const Files = lazy(() => import('pages/dashboard/Files'));
const Reports = lazy(() => import('pages/dashboard/Reports'));
const HourCards = lazy(() => import('pages/dashboard/HourCards'));
const Notes = lazy(() => import('pages/dashboard/Notes'));
const Invites = lazy(() => import('pages/dashboard/Invites'));
const ExpenseTypes = lazy(() => import('pages/dashboard/ExpenseTypes'));
const Clients = lazy(() => import('pages/dashboard/Clients'));
const Billing = lazy(() => import('pages/dashboard/Billing'));
const Logbook = lazy(() => import('pages/dashboard/Logbook'));
const SettingsLayout = lazy(() => import('layouts/SettingsLayout'));
const Settings = lazy(() => import('pages/settings/Settings'));
const PersonalProfile = lazy(() => import('pages/settings/PersonalProfile'));
const CompanyProfile = lazy(() => import('pages/settings/CompanyProfile'));
const BillingSettings = lazy(() => import('pages/settings/BillingSettings'));
const HolidaySettings = lazy(() => import('pages/settings/HolidaySettings'));
const LeaveCalendar = lazy(() => import('pages/dashboard/LeaveCalendar'));
const WelcomePage = lazy(() => import('pages/onboarding/WelcomePage'));

// Lazy-loaded auth pages
const SignIn = lazy(() => import('pages/authentication/SignIn'));
const SignUp = lazy(() => import('pages/authentication/SignUp'));
const ForgotPassword = lazy(() => import('pages/authentication/ForgotPassword'));
const ResetPassword = lazy(() => import('pages/authentication/ResetPassword'));
const VerifyEmail = lazy(() => import('pages/authentication/VerifyEmail'));
const ConfirmDelete = lazy(() => import('pages/authentication/ConfirmDelete'));
const GoogleCallback = lazy(() => import('pages/authentication/GoogleCallback'));
const Error404 = lazy(() => import('pages/error/Error404'));

const PwaHomeRedirect = () => {
  if (isPwa()) {
    const auth = getItemFromStore('authorization', '');
    return <Navigate to={auth ? '/projects' : '/login'} replace />;
  }
  return <Home />;
};

// The PWA starts at /projects, so an unauthenticated launch would otherwise render the dashboard,
// take a 401 and hard-reload into /login via the api-client. Redirect client-side instead.
const RequireAuth = ({ children }: PropsWithChildren) => {
  const auth = getItemFromStore('authorization', '');
  return auth ? <>{children}</> : <Navigate to="/login" replace />;
};

const LegacyProjectFilesRedirect = () => {
  const { projectId } = useParams();
  return <Navigate to={`/files?projectId=${projectId}`} replace />;
};

const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        element: (
          <MainLayoutProvider>
            <Suspense fallback={<PageFallback />}>
              <FrontPageLayout />
            </Suspense>
          </MainLayoutProvider>
        ),
        children: [
          {
            path: '/',
            element: <PwaHomeRedirect />
          },
          {
            path: '/pricing',
            element: <Pricing />
          },
          {
            path: '/product',
            element: <Product />
          },
          {
            path: '/contact',
            element: <Contact />
          },
          {
            path: '/ask-ai',
            element: <AskAssistant />
          },
          {
            path: '/privacy-policy',
            element: <PrivacyPolicy />
          },
          {
            path: '/terms',
            element: <TermsOfService />
          },
          {
            path: '*',
            element: <Error404 />
          }
        ]
      },
      {
        element: (
          <RequireAuth>
            <MainLayoutProvider>
              <ConfirmProvider>
                <Suspense fallback={<PageFallback />}>
                  <DashboardLayout />
                </Suspense>
              </ConfirmProvider>
            </MainLayoutProvider>
          </RequireAuth>
        ),
        children: [
          {
            path: '/projects',
            children: [
              {
                path: ':projectId?',
                index: true,
                element: <Projects />
              },
              {
                path: 'tasks',
                element: <Tasks />
              },
              {
                path: ':projectId/tasks',
                element: (
                  <ProjectGuard>
                    <Tasks />
                  </ProjectGuard>
                )
              },
              {
                path: 'hour-cards',
                element: <HourCards />
              },
              {
                path: ':projectId/hour-cards',
                element: (
                  <ProjectGuard>
                    <HourCards />
                  </ProjectGuard>
                )
              },
              // Files moved to /files; keep old links (bookmarks, installed PWAs) working
              {
                path: 'files',
                element: <Navigate to="/files" replace />
              },
              {
                path: ':projectId/files',
                element: <LegacyProjectFilesRedirect />
              }
              /**
              {
                path: ':projectId/plans',
                element: <Plans />
              },

              {
                path: ':projectId/chat',
                element: <Chat />
              }
              */
            ]
          },
          {
            path: 'leave-calendar',
            element: <LeaveCalendar />
          },
          {
            path: 'leave-requests/:id',
            element: <LeaveCalendar />
          },
          {
            path: 'notes',
            element: <Notes />
          },
          {
            path: 'files',
            element: <Files />
          },
          {
            path: 'reports',
            element: <Reports />
          },
          {
            path: 'members',
            element: <Members />
          },
          {
            path: 'invites',
            element: <Invites />
          },
          {
            path: 'expense-types',
            element: <ExpenseTypes />
          },
          {
            path: 'clients',
            element: <Clients />
          },
          {
            path: 'billing',
            element: <Billing />
          },
          {
            path: 'logbook',
            element: <Logbook />
          },
          {
            path: 'profile',
            element: <Navigate to="/settings/profile" replace />
          },
          {
            path: 'settings',
            element: <SettingsLayout />,
            children: [
              {
                index: true,
                element: <Settings />
              },
              {
                path: 'profile',
                element: <PersonalProfile />
              },
              {
                path: 'company',
                element: <CompanyProfile />
              },
              {
                path: 'billing',
                element: <BillingSettings />
              },
              {
                path: 'holiday',
                element: <HolidaySettings />
              }
            ]
          },
          {
            path: 'welcome',
            element: <WelcomePage />
          }
        ]
      },
      {
        path: 'login',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SignIn />
          </Suspense>
        )
      },
      {
        path: 'sign-up',
        element: (
          <Suspense fallback={<PageFallback />}>
            <SignUp />
          </Suspense>
        )
      },
      {
        path: 'verify-email',
        element: (
          <Suspense fallback={<PageFallback />}>
            <VerifyEmail />
          </Suspense>
        )
      },
      {
        path: 'confirm-delete',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ConfirmDelete />
          </Suspense>
        )
      },
      {
        path: 'forgot-password',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ForgotPassword />
          </Suspense>
        )
      },
      {
        path: 'reset-password',
        element: (
          <Suspense fallback={<PageFallback />}>
            <ResetPassword />
          </Suspense>
        )
      },
      {
        path: 'auth/callback',
        element: (
          <Suspense fallback={<PageFallback />}>
            <GoogleCallback />
          </Suspense>
        )
      }
    ]
  }
];

export const router = createBrowserRouter(routes);

export default routes;
